
import React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { BackgroundPattern, getBackgroundPatternStyle } from './section/BackgroundPatterns';
import { MagicBackgroundElements, getMagicBackgroundStyle } from './section/MagicBackground';
import { ContainerWidth, SectionContainer } from './section/SectionContainer';

/**
 * Props for the SectionLayout component
 */
interface SectionLayoutProps {
  /** Main content of the section */
  children: React.ReactNode;
  /** Additional CSS classes to apply to the section */
  className?: string;
  /** Background color class (Tailwind) */
  backgroundColor?: string;
  /** Whether the section should take up the full viewport height minus header */
  fullHeight?: boolean;
  /** Padding classes for top and bottom (Tailwind) */
  paddingY?: string;
  /** Control the max-width of the inner container */
  containerWidth?: ContainerWidth;
  /** Optional decorative elements to position absolutely */
  decorativeElements?: React.ReactNode;
  /** Optional ID for targeting with scroll or links */
  id?: string;
  /** Optional background color for the container */
  containerBackgroundColor?: string;
  /** Optional background pattern style */
  backgroundPattern?: BackgroundPattern;
  /** Optional pattern color (overrides the default) */
  patternColor?: string;
  /** Pattern opacity (0-100) */
  patternOpacity?: number;
  /** Enable magic background effects */
  enableMagicBackground?: boolean;
  /** Mobile specific padding override */
  mobilePaddingY?: string;
}

/**
 * SectionLayout component for creating full-width background sections
 * with configurable height, padding, and container width.
 * 
 * Features:
 * - Configurable background color
 * - Optional full-height mode
 * - Adjustable vertical padding with mobile override
 * - Multiple container width presets
 * - Support for decorative background elements
 * - Container background color option
 * - Enhanced background pattern options
 * - Magic background effect option
 */
const SectionLayout: React.FC<SectionLayoutProps> = ({
  children,
  className = "",
  backgroundColor = "bg-white",
  fullHeight = false,
  paddingY = "py-12",
  mobilePaddingY,
  containerWidth = 'medium',
  decorativeElements,
  id,
  containerBackgroundColor,
  backgroundPattern = 'none',
  patternColor,
  patternOpacity = 5,
  enableMagicBackground = false
}) => {
  const isMobile = useIsMobile();

  // Calculate height styles for full height sections
  const heightStyle = fullHeight ? {
    minHeight: isMobile ? 'calc(100vh - 5rem)' : 'calc(100vh - 10rem)'
  } : undefined;

  // Determine padding based on mobile state
  const sectionPadding = isMobile && mobilePaddingY ? mobilePaddingY : paddingY;

  // Default pattern color that can be overridden by the patternColor prop
  const defaultPatternColor = patternColor || '#003a70';

  // Convert opacity percentage to decimal
  const patternOpacityValue = (patternOpacity / 100).toFixed(2);

  // Get background pattern styles
  const patternStyle = getBackgroundPatternStyle({
    pattern: backgroundPattern,
    patternColor: defaultPatternColor,
    patternOpacity: patternOpacityValue
  });

  // Get magic background styles
  const magicStyle = getMagicBackgroundStyle({
    enabled: enableMagicBackground,
    color: defaultPatternColor
  });
  
  return (
    <section
      id={id}
      className={cn(backgroundColor, sectionPadding, "relative overflow-hidden", className)}
      style={{ ...heightStyle, ...patternStyle, ...magicStyle }}
    >
      {enableMagicBackground && (
        <MagicBackgroundElements show={enableMagicBackground} color={defaultPatternColor} />
      )}
      
      {decorativeElements && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {decorativeElements}
        </div>
      )}
      
      <SectionContainer
        containerWidth={containerWidth}
        containerBackgroundColor={containerBackgroundColor}
      >
        {children}
      </SectionContainer>
    </section>
  );
};

export default SectionLayout;
